import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { 
  TrendingDown, DollarSign, IndianRupee, Activity, Zap, BarChart3, ArrowDownRight, RefreshCw
} from 'lucide-react';
import { billingAPI } from '../../lib/api';
import { useBillingCurrency } from '../../hooks/useBillingCurrency';

export function LlmCostTab() {
  const billing = useBillingCurrency();
  const MoneyIcon = billing.currency === 'INR' ? IndianRupee : DollarSign;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await billingAPI.getLlmCostDashboard(days);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [days]);

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-48" /><Skeleton className="h-48" /><Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        No LLM cost data available yet. Data appears after AI features are used.
      </div>
    );
  }

  const { overview, by_model, by_action, daily_trend } = data;

  return (
    <div className="space-y-6 p-1" data-testid="llm-cost-dashboard">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <Button 
              key={d} 
              variant={days === d ? "default" : "outline"} 
              size="sm"
              onClick={() => setDays(d)}
              data-testid={`period-${d}d`}
            >
              {d}d
            </Button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={fetchData} data-testid="refresh-llm-costs">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="total-cost-card">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <MoneyIcon className="h-3.5 w-3.5" /> Total LLM Cost
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{billing.format(overview.total_cost_usd, { maxFractionDigits: 2 })}</div>
            <p className="text-xs text-muted-foreground">{overview.total_calls} calls in {overview.period_days}d</p>
          </CardContent>
        </Card>

        <Card data-testid="avg-cost-card">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Activity className="h-3.5 w-3.5" /> Avg Cost/Call
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{billing.format(overview.avg_cost_per_call, { maxFractionDigits: 4 })}</div>
            <p className="text-xs text-muted-foreground">Across all models</p>
          </CardContent>
        </Card>

        <Card data-testid="savings-card">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <TrendingDown className="h-3.5 w-3.5" /> Est. Savings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-500">
              {overview.direct_key_savings_pct}%
            </div>
            <p className="text-xs text-muted-foreground">
              vs Universal Key ({billing.format(overview.estimated_old_cost_usd, { maxFractionDigits: 2 })})
            </p>
          </CardContent>
        </Card>

        <Card data-testid="daily-avg-card">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Zap className="h-3.5 w-3.5" /> Daily Average
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {billing.format(overview.total_cost_usd / Math.max(overview.period_days, 1), { maxFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">/day projected</p>
          </CardContent>
        </Card>
      </div>

      {/* Cost by Model + Cost by Action */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="cost-by-model">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Cost by Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            {by_model && by_model.length > 0 ? (
              <div className="space-y-3">
                {by_model.map((item, i) => {
                  const maxCost = by_model[0]?.cost_usd || 1;
                  const pct = Math.max((item.cost_usd / maxCost) * 100, 4);
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium truncate max-w-[200px]">{item.model}</span>
                        <span className="text-muted-foreground">
                          {billing.format(item.cost_usd, { maxFractionDigits: 3 })} ({item.calls} calls)
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No model data yet</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="cost-by-action">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowDownRight className="h-4 w-4" /> Top Actions by Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            {by_action && by_action.length > 0 ? (
              <div className="space-y-2">
                {by_action.slice(0, 8).map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs font-mono">
                        {item.calls}x
                      </Badge>
                      <span className="truncate max-w-[180px]">{item.action}</span>
                    </div>
                    <span className="font-medium">{billing.format(item.cost_usd, { maxFractionDigits: 3 })}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No action data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend */}
      {daily_trend && daily_trend.length > 0 && (
        <Card data-testid="daily-trend">
          <CardHeader>
            <CardTitle className="text-base">Daily Cost Trend</CardTitle>
            <CardDescription>LLM spend over the last {days} days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {daily_trend.map((day, i) => {
                const maxCost = Math.max(...daily_trend.map(d => d.cost_usd || 0), 0.01);
                const h = Math.max((day.cost_usd / maxCost) * 100, 2);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${day.date}: ${billing.format(day.cost_usd, { maxFractionDigits: 3 })} (${day.calls} calls)`}>
                    <div 
                      className="w-full bg-blue-500/80 rounded-t hover:bg-blue-400 transition-colors cursor-default"
                      style={{ height: `${h}%`, minHeight: '2px' }}
                    />
                    {i % Math.ceil(daily_trend.length / 7) === 0 && (
                      <span className="text-[9px] text-muted-foreground rotate-0">
                        {day.date?.slice(5)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
